import React, { useEffect, useRef, useState } from "react";
import { PageHeader, Card, Tag } from "../components/UI";
import { Upload, FileText, Trash2, Download, Loader2, MessageCircle, Eye, X } from "lucide-react";
import api, { formatError } from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ProductCatalog() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null); // { product, downloads, total }
  const inputRef = useRef(null);

  async function fetchProducts() {
    const { data } = await api.get("/products");
    setItems(data);
  }
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/products");
        if (alive) setItems(data);
      } catch { /* swallow */ }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!file || !title.trim()) { alert("Title and PDF file are required."); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    if (description) fd.append("description", description);
    fd.append("file", file);
    try {
      await api.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTitle(""); setDescription(""); setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await fetchProducts();
    } catch (e) { alert(formatError(e)); }
    finally { setBusy(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this catalog entry?")) return;
    await api.delete(`/products/${id}`);
    await fetchProducts();
  }

  async function viewLog(p) {
    try {
      const { data } = await api.get(`/products/${p.id}/downloads?limit=200`);
      setLog(data);
    } catch (e) { alert(formatError(e)); }
  }

  const totalDownloads = items.reduce((s, x) => s + (x.download_count || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Network"
        title="Product Catalog"
        subtitle="Upload PDF catalogs that physiotherapists can download directly from their WhatsApp menu (option 6 — Product Catalog). Every WhatsApp pull is tracked so HO Admin & Super Admin can see real engagement."
        testId="products-page"
      />

      <Card>
        <h3 className="text-[14px] font-medium mb-3">Add new catalog</h3>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="product-form">
          <div className="md:col-span-2">
            <label className="small-caps text-slate-500">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200}
              placeholder="e.g. ZIXA Strong — Product Brochure 2026" data-testid="product-title" />
          </div>
          <div className="md:col-span-2">
            <label className="small-caps text-slate-500">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500}
              placeholder="Short blurb shown alongside the title" data-testid="product-description" />
          </div>
          <div className="md:col-span-2">
            <label className="small-caps text-slate-500">PDF file</label>
            <input ref={inputRef} type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} required data-testid="product-file" />
            {file && <div className="text-[11px] text-slate-500 mt-1">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="product-upload-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {busy ? "Uploading…" : "Upload catalog"}
            </button>
          </div>
        </form>
      </Card>

      <Card>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h3 className="text-[14px] font-medium">Active catalogs ({items.length})</h3>
            <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
              <MessageCircle size={12} /> Auto-served on the physio WhatsApp menu under <b className="text-slate-700">6 · Product Catalog</b>.
            </div>
          </div>
          <Tag tone="teal" data-testid="total-downloads">{totalDownloads} total downloads</Tag>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-[13px]">No catalogs yet. Upload your first PDF above.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((p) => (
              <div key={p.id} className="py-3 flex items-center gap-3" data-testid={`product-${p.id}`}>
                <FileText size={20} className="text-rose-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{p.description || p.original_filename}</div>
                </div>
                <Tag tone="slate">{(p.size_bytes/1024).toFixed(1)} KB</Tag>
                <button onClick={() => viewLog(p)} className="text-[12px] inline-flex items-center gap-1 text-slate-600 hover:text-teal-700" title="View download log" data-testid={`product-views-${p.id}`}>
                  <Eye size={13} /> <span className="numerals font-medium">{p.download_count || 0}</span>
                  <span className="text-slate-400">views</span>
                </button>
                <a href={`${API_BASE}/api/products/${p.id}/download?src=web`} target="_blank" rel="noreferrer"
                  className="text-teal-700 hover:underline text-[12px] inline-flex items-center gap-1" data-testid={`product-download-${p.id}`}>
                  <Download size={13} /> Download
                </a>
                <button onClick={() => del(p.id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded" data-testid={`product-delete-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {log && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setLog(null)}>
          <div className="bg-white rounded-xl border border-slate-200 w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="download-log-modal">
            <div className="p-5 border-b border-slate-100 flex items-start justify-between">
              <div>
                <h3 className="serif-display text-2xl">Download log</h3>
                <p className="text-[12.5px] text-slate-500 mt-1">{log.product?.title} · <span className="numerals font-medium">{log.total}</span> total downloads</p>
              </div>
              <button onClick={() => setLog(null)} className="text-slate-400 hover:text-slate-700" data-testid="download-log-close"><X size={18} /></button>
            </div>
            <div className="p-5 overflow-auto">
              {log.downloads.length === 0 ? (
                <div className="text-slate-400 text-sm text-center py-12">No downloads yet.</div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead className="text-slate-500 small-caps">
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2">When</th>
                      <th className="text-left py-2">Source</th>
                      <th className="text-left py-2">Mobile</th>
                      <th className="text-left py-2">User agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.downloads.map((d) => (
                      <tr key={d.id} className="border-b border-slate-50">
                        <td className="py-2 numerals">{d.at ? new Date(d.at).toLocaleString() : "—"}</td>
                        <td className="py-2"><Tag tone={d.source === "whatsapp" ? "emerald" : "slate"}>{d.source || "web"}</Tag></td>
                        <td className="py-2 font-mono text-[11px]">{d.mobile || "—"}</td>
                        <td className="py-2 text-slate-500 truncate max-w-md">{d.user_agent || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
